import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, Loader2, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading');
  const [paymentData, setPaymentData] = useState(null);
  const [error, setError] = useState(null);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      setError('Sessione di pagamento non trovata');
      return;
    }

    const checkPaymentStatus = async (attempts = 0) => {
      const maxAttempts = 5;
      const pollInterval = 2000;

      if (attempts >= maxAttempts) {
        setStatus('timeout');
        return;
      }

      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/payments/status/${sessionId}`
        );
        
        if (!response.ok) {
          throw new Error('Errore nel controllo del pagamento');
        }

        const data = await response.json();
        setPaymentData(data);

        if (data.payment_status === 'paid') {
          setStatus('success');
          return;
        } else if (data.status === 'expired') {
          setStatus('expired');
          return;
        }

        // Continue polling
        setTimeout(() => checkPaymentStatus(attempts + 1), pollInterval);
      } catch (err) {
        console.error('Error checking payment:', err);
        if (attempts < maxAttempts - 1) {
          setTimeout(() => checkPaymentStatus(attempts + 1), pollInterval);
        } else {
          setStatus('error');
          setError(err.message);
        }
      }
    };

    checkPaymentStatus();
  }, [sessionId]);

  return (
    <div className="min-h-screen pt-24 pb-16 bg-[#0a0a0a] flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        {status === 'loading' && (
          <div className="text-center bg-[#121212] rounded-2xl border border-white/10 p-8">
            <Loader2 className="w-16 h-16 text-[#E91E63] mx-auto mb-4 animate-spin" />
            <h1 className="text-2xl font-bold text-white mb-2">
              Verifica pagamento in corso...
            </h1>
            <p className="text-white/60">
              Attendi mentre confermiamo il tuo pagamento
            </p>
          </div>
        )}

        {status === 'success' && (
          <div className="text-center bg-[#121212] rounded-2xl border border-[#25D366]/30 p-8">
            <div className="w-20 h-20 bg-[#25D366]/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-12 h-12 text-[#25D366]" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">
              Pagamento confermato!
            </h1>
            <p className="text-white/60 mb-6">
              Grazie per il tuo acquisto. La tua prenotazione è stata registrata.
            </p>
            
            {paymentData?.metadata && (
              <div className="bg-white/5 rounded-xl p-4 mb-6 text-left">
                <h3 className="text-white font-semibold mb-3">Riepilogo prenotazione:</h3>
                <div className="space-y-2 text-sm">
                  <p className="text-white/80">
                    <span className="text-white/50">Nome:</span> {paymentData.metadata.nome} {paymentData.metadata.cognome}
                  </p>
                  <p className="text-white/80">
                    <span className="text-white/50">Data:</span> {paymentData.metadata.date}
                  </p>
                  <p className="text-white/80">
                    <span className="text-white/50">Biglietti:</span> {paymentData.metadata.total_tickets}
                  </p>
                  <p className="text-[#FFD700] font-bold">
                    Totale pagato: €{(paymentData.amount_total / 100).toFixed(2)}
                  </p>
                </div>
              </div>
            )}
            
            <div className="bg-[#E91E63]/10 border border-[#E91E63]/30 rounded-xl p-4 mb-6">
              <p className="text-[#E91E63] font-medium text-sm">
                📧 Ti abbiamo inviato un'email di conferma con tutti i dettagli della prenotazione.
              </p>
              <p className="text-white/50 text-xs mt-1">
                Controlla anche la cartella spam se non la trovi.
              </p>
            </div>
            
            <Link to="/">
              <Button className="w-full py-6 text-lg font-bold bg-[#E91E63] hover:bg-[#C2185B] text-white rounded-full">
                Torna alla Home
              </Button>
            </Link>
          </div>
        )}

        {status === 'expired' && (
          <div className="text-center bg-[#121212] rounded-2xl border border-yellow-500/30 p-8">
            <AlertCircle className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-white mb-2">
              Sessione scaduta
            </h1>
            <p className="text-white/60 mb-6">
              La sessione di pagamento è scaduta. Per favore riprova.
            </p>
            <Link to="/">
              <Button className="w-full py-6 text-lg font-bold bg-[#E91E63] hover:bg-[#C2185B] text-white rounded-full">
                Torna alla Home
              </Button>
            </Link>
          </div>
        )}

        {status === 'timeout' && (
          <div className="text-center bg-[#121212] rounded-2xl border border-yellow-500/30 p-8">
            <AlertCircle className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-white mb-2">
              Verifica in corso
            </h1>
            <p className="text-white/60 mb-6">
              Il pagamento potrebbe essere ancora in elaborazione. Controlla la tua email per la conferma.
            </p>
            <Link to="/">
              <Button className="w-full py-6 text-lg font-bold bg-[#E91E63] hover:bg-[#C2185B] text-white rounded-full">
                Torna alla Home
              </Button>
            </Link>
          </div>
        )}

        {status === 'error' && (
          <div className="text-center bg-[#121212] rounded-2xl border border-red-500/30 p-8">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-white mb-2">
              Si è verificato un errore
            </h1>
            <p className="text-white/60 mb-6">
              {error || 'Non è stato possibile verificare il pagamento.'}
            </p>
            <Link to="/">
              <Button className="w-full py-6 text-lg font-bold bg-[#E91E63] hover:bg-[#C2185B] text-white rounded-full">
                Torna alla Home
              </Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;
